// Sale file (schema 11): one record per sale that shows the distribution rules were followed — the client's demands
// and needs, the recommendation (when advice is given), and the pre-contractual information that was handed over.
// It follows the Insurance Distribution Directive (IDD, Directive (EU) 2016/97) as transposed in Cyprus by the Law on
// Insurance and Reinsurance Services 38(I)/2016 (as amended). The list is an aid for the advisor's own file, not legal
// advice: the insurer's compliance rules and the ICCS guidance come first.
//
//   every sale     identity & registration of the intermediary, whether advice is given, complaints and out-of-court
//                  redress (IDD art. 18) · capacity, insurers represented and nature of remuneration (art. 19) ·
//                  demands and needs recorded, product consistent with them (art. 20(1)) · product information
//                  document (IPID for general insurance, art. 20(5); the insurer's pre-contractual information for life)
//                  · information on paper or another durable medium, a copy to the client (art. 23) · data-protection
//                  notice · signed proposal
//   advised        a personalised recommendation explaining why the product best meets the demands and needs (art. 20(1))
//   life / IBIP    know-your-client / anti-money-laundering file complete (the client's KYC file, schema 10)
//   IBIP           suitability assessment — knowledge & experience, financial situation incl. ability to bear losses,
//                  objectives incl. risk tolerance (art. 30(1)) · suitability statement before the contract (art. 30(5))
//                  · costs and associated charges (art. 29(1)) · the key information document (PRIIPs KID)
//
// Nothing is ever deleted; a file is "complete" when every item required for its kind of sale is there. Policies sold in
// the last 12 months without a sale file are listed so nothing is forgotten.
import { DomainError, created, opt, put, timeline, touched, type Ctx } from './cmd.ts';
import type { Changeset, Put, WorkspaceView } from './changeset.ts';
import { diffDays, normalizeDay, type Day } from './dates.ts';
import { isActivePolicy } from './rules.ts';
import { kycStatus } from './kyc.ts';
import { isGeneralPolicy } from './gi-renewals.ts';
import { SALE_ADVICE, SALE_CHANNELS, SALE_LINES, type Policy, type SaleAdvice, type SaleChannel, type SaleCheck, type SaleFile, type SaleLine } from './schema.ts';

export const SALE_CHECKS = [
  'identity_disclosed', 'advice_disclosed', 'complaints_info', 'capacity_disclosed', 'remuneration_disclosed',
  'demands_needs', 'recommendation', 'product_info', 'kid', 'costs_disclosed', 'suitability', 'suitability_report',
  'kyc', 'privacy_notice', 'durable_medium', 'signed',
] as const;
export type SaleCheckKey = typeof SALE_CHECKS[number];
/** items filled in from the record itself (texts, the KYC file) rather than ticked */
export const AUTO_SALE_CHECKS: readonly SaleCheckKey[] = ['demands_needs', 'recommendation', 'kyc'];

/** which items a sale of this kind needs */
export function requiredSaleChecks(line: SaleLine, advice: SaleAdvice): SaleCheckKey[] {
  const out: SaleCheckKey[] = ['identity_disclosed', 'advice_disclosed', 'complaints_info', 'capacity_disclosed', 'remuneration_disclosed', 'demands_needs'];
  // insurance-based investment products are always sold with a suitability assessment (advice) in this file
  if (advice === 'advised' || line === 'ibip') out.push('recommendation');
  out.push('product_info');
  if (line === 'ibip') out.push('kid', 'costs_disclosed', 'suitability', 'suitability_report');
  if (line === 'life' || line === 'ibip') out.push('kyc');
  out.push('privacy_notice', 'durable_medium', 'signed');
  return out;
}

export interface SaleFileState { required: SaleCheckKey[]; done: SaleCheckKey[]; missing: SaleCheckKey[]; percent: number; complete: boolean }
export function saleFileState(v: WorkspaceView, f: Pick<SaleFile, 'personId' | 'line' | 'advice' | 'demands' | 'recommendation' | 'checks'>, today: Day): SaleFileState {
  const required = requiredSaleChecks(f.line, f.advice);
  const checks = f.checks ?? {};
  const isDone = (k: SaleCheckKey): boolean => {
    if (k === 'demands_needs') return (opt(f.demands)?.length ?? 0) >= 10;
    if (k === 'recommendation') return (opt(f.recommendation)?.length ?? 0) >= 10;
    if (k === 'kyc') return kycStatus(v, f.personId, today).status === 'complete';
    return !!checks[k]?.at;
  };
  const done = required.filter(isDone);
  const missing = required.filter((k) => !done.includes(k));
  return { required, done, missing, percent: Math.round((done.length / required.length) * 100), complete: !missing.length };
}

/** the kind of sale suggested for a policy: general branch/category → general; savings / pension / investment → IBIP; else life */
export function suggestSaleLine(p: Pick<Policy, 'branch' | 'category' | 'productName'>): SaleLine {
  if (isGeneralPolicy(p)) return 'general';
  if (p.category === 'savings' || p.category === 'pension' || /unit|invest|επενδυ|ταμιευ|savings|инвест/i.test(p.productName ?? '')) return 'ibip';
  return 'life';
}

export interface SaleFileInput {
  id?: string; personId: string; policyId?: string | null; caseId?: string | null; needsAnalysisId?: string | null;
  line: SaleLine; advice?: SaleAdvice; channel?: SaleChannel; product?: string | null; insurer?: string | null; saleDate?: string | null;
  demands?: string | null; recommendation?: string | null; checks?: Record<string, SaleCheck | null> | null; notes?: string | null;
  /** v12: the client's risk profile the suitability assessment relied on */
  riskProfileId?: string | null;
}
export function saveSaleFile(v: WorkspaceView, input: SaleFileInput, ctx: Ctx): Changeset {
  const cur = input.id ? v.get('sale_files', input.id) : undefined;
  if (input.id && !cur) throw new DomainError('sale_file.not_found');
  const person = v.get('persons', input.personId);
  if (!person) throw new DomainError('person.not_found');
  if (cur && cur.personId !== person.id) throw new DomainError('sale_file.person');
  if (!(SALE_LINES as readonly string[]).includes(input.line)) throw new DomainError('sale_file.line');
  const advice = input.advice ?? cur?.advice ?? 'advised';
  if (!(SALE_ADVICE as readonly string[]).includes(advice)) throw new DomainError('sale_file.advice');
  const channel = input.channel ?? cur?.channel ?? 'face_to_face';
  if (!(SALE_CHANNELS as readonly string[]).includes(channel)) throw new DomainError('sale_file.channel');
  let policy: Policy | undefined;
  if (input.policyId) {
    policy = v.get('policies', input.policyId);
    if (!policy || policy.personId !== person.id) throw new DomainError('sale_file.policy');
    const other = v.where('sale_files', 'policyId', policy.id).find((s) => s.id !== cur?.id);
    if (other) throw new DomainError('sale_file.policy_has_file');
  }
  if (input.needsAnalysisId) { const n = v.get('needs_analyses', input.needsAnalysisId); if (!n || n.personId !== person.id) throw new DomainError('sale_file.needs'); }
  if (input.caseId) { const c = v.get('cases', input.caseId); if (!c || c.personId !== person.id) throw new DomainError('sale_file.case'); }
  if (input.riskProfileId) { const r = v.get('risk_profiles', input.riskProfileId); if (!r || r.personId !== person.id) throw new DomainError('sale_file.risk_profile'); }
  const saleDate = normalizeDay(input.saleDate ?? cur?.saleDate ?? policy?.startDate ?? policy?.issueDate ?? ctx.today);
  if (!saleDate) throw new DomainError('sale_file.date');
  if (saleDate > ctx.today) throw new DomainError('sale_file.future');
  // checklist items: only known keys; a date not in the future; documents of this client only
  const checks: Record<string, SaleCheck> = { ...(cur?.checks ?? {}) };
  for (const [k, val] of Object.entries(input.checks ?? {})) {
    if (!(SALE_CHECKS as readonly string[]).includes(k) || AUTO_SALE_CHECKS.includes(k as SaleCheckKey)) continue;
    if (!val || !val.at) { delete checks[k]; continue; }
    const at = normalizeDay(val.at);
    if (!at || at > ctx.today) throw new DomainError('sale_file.check_date');
    if (val.documentId) { const d = v.get('documents', val.documentId); if (!d || d.personId !== person.id) throw new DomainError('sale_file.document'); }
    checks[k] = { at, documentId: val.documentId || null, note: opt(val.note) };
  }
  const fields = {
    personId: person.id, policyId: input.policyId !== undefined ? input.policyId || null : cur?.policyId ?? null,
    caseId: input.caseId !== undefined ? input.caseId || null : cur?.caseId ?? null,
    needsAnalysisId: input.needsAnalysisId !== undefined ? input.needsAnalysisId || null : cur?.needsAnalysisId ?? null,
    line: input.line, advice: advice as SaleAdvice, channel: channel as SaleChannel,
    product: input.product !== undefined ? opt(input.product) : cur?.product ?? opt(policy?.productName),
    insurer: input.insurer !== undefined ? opt(input.insurer) : cur?.insurer ?? opt(policy?.carrier),
    saleDate,
    demands: input.demands !== undefined ? opt(input.demands) : cur?.demands ?? null,
    recommendation: input.recommendation !== undefined ? opt(input.recommendation) : cur?.recommendation ?? null,
    checks, notes: input.notes !== undefined ? opt(input.notes) : cur?.notes ?? null,
    riskProfileId: input.riskProfileId !== undefined ? input.riskProfileId || null : cur?.riskProfileId ?? null,
  };
  const state = saleFileState(v, fields, ctx.today);
  const status = state.complete ? 'complete' as const : 'open' as const;
  const completedAt = state.complete ? cur?.completedAt ?? ctx.now : null;
  const row: SaleFile = cur ? touched(ctx, cur, { ...fields, status, completedAt }) : created<'sale_files'>(ctx, { ...fields, status, completedAt });
  const puts: Put[] = [put('sale_files', row)];
  if (!cur || cur.status !== status) puts.push(timeline(ctx, person.id, 'policy', status === 'complete' ? 'timeline.sale_file_complete' : cur ? 'timeline.sale_file_reopened' : 'timeline.sale_file_created', row.product ?? null, { table: 'sale_files', id: row.id }));
  return { action: cur ? 'sale_file.update' : 'sale_file.create', entity: 'sale_files', entityId: row.id, summary: person.displayName, puts, deletes: [] };
}

export interface SaleFileOverview { open: { file: SaleFile; state: SaleFileState }[]; complete: number; withoutFile: Policy[] }
/** open files with what is missing, and active policies that started in the last `days` (365) without a sale file */
export function saleFilesOverview(v: WorkspaceView, today: Day, opts: { days?: number } = {}): SaleFileOverview {
  const days = opts.days ?? 365;
  const files = v.rows('sale_files');
  const open = files.filter((f) => f.status !== 'complete').map((file) => ({ file, state: saleFileState(v, file, today) })).sort((a, b) => b.file.saleDate.localeCompare(a.file.saleDate));
  const covered = new Set(files.map((f) => f.policyId).filter(Boolean));
  const withoutFile = v.rows('policies').filter((p) => {
    if (!isActivePolicy(p) || covered.has(p.id)) return false;
    const start = p.startDate ?? p.issueDate;
    if (!start || start > today) return false;
    const person = v.get('persons', p.personId);
    return !!person && !person.archivedAt && diffDays(start, today) <= days;
  }).sort((a, b) => (b.startDate ?? b.issueDate ?? '').localeCompare(a.startDate ?? a.issueDate ?? ''));
  return { open, complete: files.length - open.length, withoutFile };
}
